/** Root guidance file for humans and coding agents working in a vault checkout. */
export const AGENTS_MD_PATH = 'AGENTS.md';

export const DEFAULT_AGENTS_MD = `# Trove

This directory is an encrypted Trove vault — a personal records store synced as ciphertext, not a conventional GitHub project.

## CLI

\`\`\`sh
npm i -g @fldr/trove
trove auth login
trove clone ~/my-repo
\`\`\`

\`trove clone\` takes any directory. After that, use normal Git:

\`\`\`sh
git pull --rebase
git push
\`\`\`

## History

- Linear history on \`main\` only
- No feature branches, merge commits, or force-pushes
- Prefer \`git pull --rebase\` before \`git push\`

## Layout

- \`private/\` — your personal files
- \`shared/\` — folders others have shared with you
- \`.trv\` — hidden annotation metadata (icons, colors, tags); leave it alone unless you intend to edit that schema

## Editing

Prefer clear folders and markdown notes. PDFs, images, and other binaries are fine. This file is seeded once by Trove and will not be overwritten if you change it.
`;

export type AgentsGuideFile = {
  path: string;
  content?: string;
  mimeType: string;
  size: number;
  modifiedAt: string;
};

const encoder = new TextEncoder();

export const hasAgentsGuide = (files: Array<{ path: string }>): boolean =>
  files.some((file) => file.path === AGENTS_MD_PATH);

export const agentsGuideFile = (modifiedAt = new Date().toISOString()): AgentsGuideFile => ({
  path: AGENTS_MD_PATH,
  content: DEFAULT_AGENTS_MD,
  mimeType: 'text/markdown',
  size: encoder.encode(DEFAULT_AGENTS_MD).length,
  modifiedAt,
});

/** Insert default `AGENTS.md` when missing. Never overwrites an existing file. */
export const withAgentsGuide = <T extends { path: string }>(
  files: T[],
  modifiedAt = new Date().toISOString(),
): Array<T | AgentsGuideFile> => {
  if (hasAgentsGuide(files)) return files;
  return [...files, agentsGuideFile(modifiedAt)];
};

export const ensureAgentsGuide = <T extends { files: Array<{ path: string }> }>(snapshot: T): T => {
  if (hasAgentsGuide(snapshot.files)) return snapshot;
  return { ...snapshot, files: withAgentsGuide(snapshot.files) as T['files'] };
};
